import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isWithinBounds,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { DLG_ECM_DIVISOR, DLG_ECM_FAT, DLG_ECM_OFFSET, DLG_ECM_PROTEIN } from "consts";

import { ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION, FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION } from "models";

import { Card, Flex, Form, FormInstance, Icon, InputNumber, KlimRadioGroup, PageSpin, Typography } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const MilkYieldForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [milkProduction, setMilkProduction] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION | null>(null);
  const [totalMilkYieldKgPerCow, setTotalMilkYieldKgPerCow] = useState<number | null>(null);
  const [fatContentPct, setFatContentPct] = useState<number | null>(null);
  const [proteinContentPct, setProteinContentPct] = useState<number | null>(null);

  const { data: dairyPrimaryDataQuery, isLoading, isSuccess } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      milkProduction: milkProduction,
      milkYieldKg: totalMilkYieldKgPerCow,
      fatContentPct: fatContentPct,
      proteinContentPct: proteinContentPct,
      isSubmitted: false,
    },
    primaryDataId
  );

  const milkYieldAmountECM = useMemo(
    () =>
      totalMilkYieldKgPerCow && fatContentPct != null && proteinContentPct != null
        ? (totalMilkYieldKgPerCow * (DLG_ECM_FAT * (fatContentPct * 100) + DLG_ECM_PROTEIN * (proteinContentPct * 100) + DLG_ECM_OFFSET)) / DLG_ECM_DIVISOR
        : null,
    [totalMilkYieldKgPerCow, fatContentPct, proteinContentPct]
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData({});
      }
    },
    [isLoading, mutateDairyPrimaryData, readOnly]
  );

  useEffect(() => {
    if (dairyPrimaryDataQuery && isSuccess) {
      setMilkProduction(dairyPrimaryDataQuery.milkProduction ?? null);
      setTotalMilkYieldKgPerCow(dairyPrimaryDataQuery.milkYieldKg ?? null);
      setFatContentPct(dairyPrimaryDataQuery.fatContentPct ?? null);
      setProteinContentPct(dairyPrimaryDataQuery.proteinContentPct ?? null);

      formRef.current?.setFieldsValue({
        MilkYieldKgPerCow: dairyPrimaryDataQuery.milkYieldKg ?? null,
        fatContentPct: formatFractionInput(dairyPrimaryDataQuery.fatContentPct ?? null),
        proteinContentPct: formatFractionInput(dairyPrimaryDataQuery.proteinContentPct ?? null),
      });

      const fieldsToValidate = (["milkYieldKg", "fatContentPct", "proteinContentPct"] as const).some((field) => dairyPrimaryDataQuery[field] != null);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-dairy-milk-yield-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("dairyMilkYieldForm.question.milkYieldRadioQuestion")}
              <span className="required-asterisk">*</span>
              {/* <Tooltip title={"tool tip coming soon"}> */}
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              {/* </Tooltip> */}
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-dairy-selfProducted-delivered-radio"
              onChange={({ target }) => {
                setMilkProduction(target.value);
              }}
              value={milkProduction}
              optionType="button"
              options={[FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION.PRODUCED, FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION.DELIVERED].map((milkProduction) => ({
                label: t(`dairyMilkYieldForm.milkYieldProductionOptions.${milkProduction}`),
                value: milkProduction,
              }))}
            />
          </Flex>

          <InputNumber
            disabled={readOnly}
            name="MilkYieldKgPerCow"
            dataTestId="footprinting-dairy-milk-yield-weight-input"
            label={t("dairyMilkYieldForm.question.milkYieldAmountLabel")}
            suffix={t("dairyMilkYieldForm.question.milkYieldAmountSuffix")}
            value={totalMilkYieldKgPerCow}
            initialValue={totalMilkYieldKgPerCow}
            onChange={(v) => setTotalMilkYieldKgPerCow(v)}
            validateFirst
            rules={[
              { required: true, message: t("dairyMilkYieldForm.error.milkYieldAmount") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isWithinBounds(v, 1, 16000)) {
                    return Promise.reject(new Error(t("dairyMilkYieldForm.error.milkYieldAmount")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />

          <Flex gap={16}>
            <InputNumber
              className="floating-label flex-1"
              dataTestId={"footprinting-farm-dairy-milk-yield-fat-content-input"}
              disabled={readOnly}
              name={"fatContentPct"}
              label={t("dairyMilkYieldForm.question.fatContentLabel")}
              suffix="%"
              value={formatFractionInput(fatContentPct)}
              initialValue={formatFractionInput(fatContentPct)}
              onChange={(v) => setFatContentPct(formatPercentageInput(v))}
              precision={2}
              validateFirst
              rules={[
                { required: true, type: "number", message: t("dairyMilkYieldForm.error.fatContentValue") },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isWithinBounds(v, 2, 8)) {
                      return Promise.reject(new Error(t("dairyMilkYieldForm.error.fatContentValue")));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            />

            <InputNumber
              className="floating-label flex-1"
              dataTestId={"footprinting-farm-dairy-milk-yield-protein-content-input"}
              disabled={readOnly}
              name={"proteinContentPct"}
              label={t("dairyMilkYieldForm.question.proteinContentLabel")}
              suffix="%"
              value={formatFractionInput(proteinContentPct)}
              initialValue={formatFractionInput(proteinContentPct)}
              onChange={(v) => setProteinContentPct(formatPercentageInput(v))}
              precision={2}
              validateFirst
              rules={[
                { required: true, type: "number", message: t("dairyMilkYieldForm.error.proteinContentValue") },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isWithinBounds(v, 2.5, 6)) {
                      return Promise.reject(new Error(t("dairyMilkYieldForm.error.proteinContentValue")));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>

          <Card border className="dairy-display-information-card padding-100 flex-center vertical">
            <Flex vertical>
              <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-25">
                {t("dairyMilkYieldForm.milkYieldDisplayTitle")}
              </Typography>
              <Typography variant="h5" className="center">
                {milkYieldAmountECM !== null ? `${milkYieldAmountECM.toFixed(0)} kg` : "0 kg"}
              </Typography>
            </Flex>
          </Card>
        </Flex>
      </Form>
    </Card>
  );
};

MilkYieldForm.Key = "dairy.milkYield";
export { MilkYieldForm };
